import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaProjectDiagram, FaChartBar } from 'react-icons/fa';
import { Project } from '../types';
import { useData } from '../contexts/DataContext';

const ProjectsContainer = styled.section`
  background: linear-gradient(135deg, 
    rgba(15, 23, 42, 0.8) 0%, 
    rgba(30, 41, 59, 0.9) 50%, 
    rgba(15, 23, 42, 0.8) 100%
  );
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;


const ProjectsContent = styled.div`
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3rem 1rem;
  }
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 2.5rem;
  position: relative;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
`;


const SectionTitle = styled(motion.h2)`
  font-size: 4rem;
  font-weight: 100;
  background: linear-gradient(135deg, 
    #ffffff 0%, 
    rgba(64, 224, 255, 0.95) 30%,
    rgba(100, 200, 255, 0.8) 70%,
    #ffffff 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;
  letter-spacing: -2px;
  position: relative;
  text-shadow: 0 0 30px rgba(64, 224, 255, 0.3);
  filter: drop-shadow(0 0 20px rgba(64, 224, 255, 0.2));
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    letter-spacing: -1px;
  }
`;




const ProjectsSection = styled.div`
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
  }
`;

const SubsectionTitle = styled(motion.h3)`
  font-size: 2rem;
  font-weight: 600;
  color: white;
  text-align: left;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.6rem;
    flex-wrap: wrap;
  }

  @media (max-width: 480px) {
    font-size: 1.4rem;
    gap: 0.3rem;
  }
`;

const SubsectionIcon = styled.div`
  color: #ffd700;
  font-size: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const ViewButton = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  margin-top: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    color: white;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
  }

  @media (max-width: 480px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const ShowMoreButton = styled.button`
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 20px auto 0;
  width: fit-content;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 13px;
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.2rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;


const ProjectCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  min-height: 220px;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    border-color: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    min-height: 200px;
  }

  @media (max-width: 480px) {
    min-height: 170px;
  }
`;





const ProjectContent = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0.75rem;

  @media (max-width: 768px) {
    padding: 1rem 1.2rem;
    gap: 0.6rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 0.9rem;
    gap: 0.4rem;
  }
`;

const ProjectTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const ProjectDescription = styled.p`
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.6;
  margin: 0;
  font-size: 0.95rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const ProjectTechnologies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0;

  @media (max-width: 480px) {
    gap: 0.4rem;
  }
`;

const TechnologyTag = styled.span<{ category?: string }>`
  background: rgba(64, 224, 255, 0.15);
  color: rgba(64, 224, 255, 0.9);
  padding: 0.3rem 0.8rem;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid rgba(64, 224, 255, 0.2);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.25rem 0.7rem;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
  }
`;


const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: white;
  font-size: 1.2rem;
`;

const LoadingSpinner = styled(motion.div)`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ffd700;
  border-radius: 50%;
  margin-right: 1rem;
`;

interface ProjectsProps {
  projects: Project[];
  onProjectSelect: (project: Project) => void;
  loading: boolean;
}

const Projects: React.FC<ProjectsProps> = ({ projects, onProjectSelect, loading }) => {
  const { skills } = useData();
  const ref = useRef(null);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [showAllDashboards, setShowAllDashboards] = useState(false);

  // Helper function to get category for a skill
  const getSkillCategory = (skillName: string): string => {
    const skill = skills.find(s => s.name === skillName);
    return skill?.category || '';
  };

  if (loading) {
    return (
      <ProjectsContainer id="projects">
        <LoadingContainer>
          <LoadingSpinner
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          Loading projects...
        </LoadingContainer>
      </ProjectsContainer>
    );
  }

  // Separate projects by category
  const projectItems = projects.filter(project => project.category === 'project');
  const dashboardItems = projects.filter(project => project.category === 'dashboard');

  const renderProjectCard = (project: Project, index: number) => (
    <ProjectCard
      key={project.id}
      onClick={() => onProjectSelect(project)}
    >
      
      <ProjectContent>
        <ProjectTitle>{project.title}</ProjectTitle>
        <ProjectDescription>{project.description}</ProjectDescription>
        
        <ProjectTechnologies>
          {project.technologies.slice(0, 3).map((tech, techIndex) => (
            <TechnologyTag key={techIndex} category={getSkillCategory(tech)}>
              {tech}
            </TechnologyTag>
          ))}
          {project.technologies.length > 3 && (
            <TechnologyTag>
              +{project.technologies.length - 3}
            </TechnologyTag>
          )}
        </ProjectTechnologies>
        
        
        <div style={{ textAlign: 'center', marginTop: 'auto' }}>
          <ViewButton>
            <span>View Details</span>
            <span>→</span>
          </ViewButton>
        </div>
      </ProjectContent>
    </ProjectCard>
  );

  return (
    <ProjectsContainer id="projects" ref={ref}>
      <ProjectsContent>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            My Work
          </SectionTitle>
        </SectionHeader>

      {/* Projects Section */}
      <ProjectsSection>
        <SubsectionTitle>
          <SubsectionIcon>
            <FaProjectDiagram />
          </SubsectionIcon>
          Projects
        </SubsectionTitle>

        <ProjectsGrid>
          {(showAllProjects ? projectItems : projectItems.slice(0, 6)).map((project, index) => renderProjectCard(project, index))}
        </ProjectsGrid>
        {projectItems.length > 6 && (
          <ShowMoreButton 
            onClick={() => setShowAllProjects(!showAllProjects)}
          >
            {showAllProjects ? (
              <>
                <span>👆</span>
                <span>Show Less</span>
              </>
            ) : (
              <>
                <span>🚀</span>
                <span>Explore More Projects</span>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>({projectItems.length - 6} more)</span>
              </>
            )}
          </ShowMoreButton>
        )}
      </ProjectsSection>

      {/* Dashboards Section */}
      <ProjectsSection>
        <SubsectionTitle>
          <SubsectionIcon>
            <FaChartBar />
          </SubsectionIcon>
          Dashboards
        </SubsectionTitle>

        <ProjectsGrid>
          {(showAllDashboards ? dashboardItems : dashboardItems.slice(0, 6)).map((project, index) => renderProjectCard(project, index))}
        </ProjectsGrid>
        {dashboardItems.length > 6 && (
          <ShowMoreButton 
            onClick={() => setShowAllDashboards(!showAllDashboards)}
          >
            {showAllDashboards ? (
              <>
                <span>👆</span>
                <span>Show Less</span>
              </>
            ) : (
              <>
                <span>📊</span>
                <span>Discover More Dashboards</span>
                <span style={{ fontSize: '12px', opacity: 0.8 }}>({dashboardItems.length - 6} more)</span>
              </>
            )}
          </ShowMoreButton>
        )}
      </ProjectsSection>
      </ProjectsContent>
    </ProjectsContainer>
  );
};

export default Projects;

